import { Checkbox as RadixCheckbox } from 'radix-ui';
import type { ReactElement, ReactNode } from 'react';

import { cx } from '../../lib/cx';
import { Icon } from '../Icon/Icon';
import styles from './checkbox.module.css';

export interface CheckboxProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /**
   * Подпись. Есть — рисуется строка «флажок + подпись», нажимается вся целиком.
   * Нет — только флажок: для строк, где подписью служит внешний `<label>`
   * с собственным содержимым (название плюс чипы).
   */
  label?: ReactNode;
  /** Пояснение под подписью. */
  hint?: ReactNode;
  className?: string;
}

/**
 * Флажок на Radix Checkbox (решение владельца 2026-09-21).
 *
 * Раньше в приложении стоял голый `<input type="checkbox">`: его рисует ОС — у каждой
 * свой размер, своя рамка и акцентный цвет из системных настроек. Этот — в стиле
 * «Табло»: квадрат 20px, ink при отметке, как главная кнопка, и строка не ниже
 * `--ds-control-min`, чтобы на планшете попадать пальцем.
 */
export function Checkbox({
  checked,
  onCheckedChange,
  disabled,
  label,
  hint,
  className,
}: CheckboxProps): ReactElement {
  const box = (
    <RadixCheckbox.Root
      className={styles.box}
      checked={checked}
      disabled={disabled}
      onCheckedChange={(next) => onCheckedChange(next === true)}
    >
      <RadixCheckbox.Indicator className={styles.indicator}>
        <Icon name="check" size={14} />
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );

  if (label === undefined) return box;

  return (
    <label className={cx(styles.row, disabled ? styles.disabled : undefined, className)}>
      {box}
      <span className={styles.text}>
        <span>{label}</span>
        {hint ? <span className={styles.hint}>{hint}</span> : null}
      </span>
    </label>
  );
}
